import * as React from 'react';

import { useMutation, useQuery } from '@apollo/client';
import { captureException } from '@sentry/react';

import { useParams } from 'js/lib/useRouter';
import { get as userGet } from 'js/lib/user';

import OrgScopedImpersonationContext from 'bundles/unified-common/contexts/org-scoped/OrgScopedImpersonationContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';
import useOrgScopedProgramMembership from 'bundles/unified-home-common/hooks/useOrgScopedProgramMembership';
import AdminProgramImpersonationsQuery from 'bundles/unified-home-common/queries/AdminProgramImpersonationsQuery.graphql';
import DeleteAdminProgramImpersonationsMutation from 'bundles/unified-home-common/queries/DeleteAdminProgramImpersonationsMutation.graphql';
import ResolveProgramsByOrganizationQuery from 'bundles/unified-home-common/queries/ResolveProgramsByOrganizationQuery.graphql';
import SetAdminProgramImpersonationsMutation from 'bundles/unified-home-common/queries/SetAdminProgramImpersonationsMutation.graphql';
import type {
  AdminProgramImpersonationsQuery as AdminProgramImpersonationsQueryType,
  AdminProgramImpersonationsQueryVariables,
} from 'bundles/unified-home-common/queries/__generated__/AdminProgramImpersonationsQuery';
import type {
  DeleteAdminProgramImpersonationsMutation as DeleteAdminProgramImpersonationsMutationType,
  DeleteAdminProgramImpersonationsMutationVariables,
} from 'bundles/unified-home-common/queries/__generated__/DeleteAdminProgramImpersonationsMutation';
import type {
  ResolveProgramsByOrganizationQuery as ResolveProgramsByOrganizationQueryData,
  ResolveProgramsByOrganizationQueryVariables,
} from 'bundles/unified-home-common/queries/__generated__/ResolveProgramsByOrganizationQuery';
import type {
  SetAdminProgramImpersonationsMutation as SetAdminProgramImpersonationsMutationType,
  SetAdminProgramImpersonationsMutationVariables,
} from 'bundles/unified-home-common/queries/__generated__/SetAdminProgramImpersonationsMutation';

/**
 * Provides org-scoped admin preview mode state plus the set of program IDs resolved for the current
 * org. Queries the active impersonation session (admin-only) and the Program Resolver API, and
 * exposes hooks to check preview state, get selected program names, set/exit preview mode, and read
 * the resolved program IDs. Resolving program IDs here (and sharing them via context) lets every
 * org-scoped home consumer reuse a single request. Must be rendered inside a provider that supplies
 * ThirdPartyOrganizationContext.
 */
const OrgScopedImpersonationDataProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const thirdPartyOrganization = useThirdPartyOrganization();
  const thirdPartyOrganizationId = thirdPartyOrganization?.id ?? '';
  const { orgSlug } = useParams();
  const userId = userGet()?.id;

  // Only org/program admins can read or mutate impersonation state, so the impersonation session
  // query and the set/exit mutations below are gated on this (the BE would reject them anyway).
  // The program resolver query is intentionally NOT gated — it serves program IDs for every user.
  const { isAdminOfAnyProgramInOrg, isLoading: isMembershipLoading } = useOrgScopedProgramMembership(
    userId,
    thirdPartyOrganization
  );
  const isAdmin = !isMembershipLoading && !!isAdminOfAnyProgramInOrg;

  const {
    data,
    loading: impersonationsLoading,
    error: impersonationsError,
  } = useQuery<AdminProgramImpersonationsQueryType, AdminProgramImpersonationsQueryVariables>(
    AdminProgramImpersonationsQuery,
    {
      variables: { thirdPartyOrganizationId },
      skip: !thirdPartyOrganizationId || !isAdmin,
    }
  );

  // Not gated on isAdmin: the resolver returns the correct program IDs for every user type — a
  // standard user's org memberships, an admin's impersonated programs (or their own memberships),
  // and for logged-out users the org's oldest public program. All org-scoped home consumers read
  // these IDs from context, so we resolve them once here for everyone with an org slug.
  const {
    data: programsData,
    loading: programsLoading,
    error: programsError,
  } = useQuery<ResolveProgramsByOrganizationQueryData, ResolveProgramsByOrganizationQueryVariables>(
    ResolveProgramsByOrganizationQuery,
    {
      variables: { orgSlug: orgSlug ?? '' },
      skip: !orgSlug,
      context: { clientName: 'gatewayGql' },
    }
  );

  React.useEffect(() => {
    if (impersonationsError) {
      captureException(impersonationsError, {
        extra: { message: 'Error fetching admin program impersonations' },
      });
    }
  }, [impersonationsError]);

  React.useEffect(() => {
    if (programsError) {
      captureException(programsError, {
        extra: { message: 'Error resolving programs by organization for impersonation banner' },
      });
    }
  }, [programsError]);

  const activeSession = data?.AdminProgramImpersonations?.get;
  // Avoid flashing the banner before the impersonations query has loaded.
  const isPreviewMode = !impersonationsLoading && !!activeSession;
  const previewProgramIds = React.useMemo(() => activeSession?.programIds ?? [], [activeSession]);

  const programs = React.useMemo(
    () => programsData?.EnterpriseProgram?.resolveProgramsByOrganization?.elements ?? [],
    [programsData]
  );

  const programNamesById = React.useMemo(() => {
    const map = new Map<string, string>();
    programs.forEach((p) => map.set(p.id, p.name));
    return map;
  }, [programs]);

  // Only resolve names once both queries have finished so we never render a
  // partial list while the programs query is still in flight.
  const previewProgramNames = React.useMemo(() => {
    if (programsLoading) return [];
    return previewProgramIds.map((id) => programNamesById.get(id)).filter((name): name is string => !!name);
  }, [previewProgramIds, programNamesById, programsLoading]);

  const [setMutation] = useMutation<
    SetAdminProgramImpersonationsMutationType,
    SetAdminProgramImpersonationsMutationVariables
  >(SetAdminProgramImpersonationsMutation);

  const [deleteMutation] = useMutation<
    DeleteAdminProgramImpersonationsMutationType,
    DeleteAdminProgramImpersonationsMutationVariables
  >(DeleteAdminProgramImpersonationsMutation);

  const setPreviewMode = React.useCallback<(programIds: string[]) => Promise<void>>(
    async (programIds: string[]) => {
      if (!isAdmin) return;
      try {
        const result = await setMutation({ variables: { input: { thirdPartyOrganizationId, programIds } } });
        const payload = result.data?.AdminProgramImpersonations_Set;
        if (payload?.__typename === 'AdminProgramImpersonations_SetResultError') {
          captureException(new Error(payload.message), {
            extra: { message: 'Failed to set admin program impersonations', thirdPartyOrganizationId },
          });
        } else {
          // Hard reload so every org-scoped query (badge collections, skill pathways, etc.)
          // re-executes against the updated impersonation session on the backend.
          window.location.reload();
        }
      } catch (err) {
        captureException(err, {
          extra: { message: 'Error setting admin program impersonations', thirdPartyOrganizationId },
        });
      }
    },
    [isAdmin, setMutation, thirdPartyOrganizationId]
  );

  const exitPreviewMode = React.useCallback<() => Promise<void>>(async () => {
    if (!isAdmin) return;
    try {
      const result = await deleteMutation({ variables: { input: { thirdPartyOrganizationId } } });
      const payload = result.data?.AdminProgramImpersonations_Delete;
      if (payload?.__typename === 'AdminProgramImpersonations_DeleteResultError') {
        captureException(new Error(payload.message), {
          extra: { message: 'Failed to delete admin program impersonations', thirdPartyOrganizationId },
        });
      } else {
        window.location.reload();
      }
    } catch (err) {
      captureException(err, {
        extra: { message: 'Error deleting admin program impersonations', thirdPartyOrganizationId },
      });
    }
  }, [isAdmin, deleteMutation, thirdPartyOrganizationId]);

  // Memoized separately from the context value below so the array identity only changes
  // when the resolved programs change, not when unrelated context fields update.
  const resolvedProgramIds = React.useMemo(() => programs.map((program) => program.id), [programs]);

  const value = React.useMemo(
    () => ({
      isAdmin,
      isPreviewMode,
      previewProgramIds,
      previewProgramNames,
      setPreviewMode,
      exitPreviewMode,
      resolvedProgramIds,
      isResolvedProgramIdsLoading: programsLoading,
    }),

    [
      isAdmin,
      isPreviewMode,
      previewProgramIds,
      previewProgramNames,
      setPreviewMode,
      exitPreviewMode,
      resolvedProgramIds,
      programsLoading,
    ]
  );

  return <OrgScopedImpersonationContext.Provider value={value}>{children}</OrgScopedImpersonationContext.Provider>;
};

export default OrgScopedImpersonationDataProvider;
